import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import WeeklyAvailabilitySelector from '../Providers/WeeklyAvailabilitySelector';
import { createConnectAccount, getConnectAccountStatus, createDashboardLink, updateBankAccount } from '../../utils/stripe';
import StripeOnboarding from './StripeOnboarding';

const ProviderProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [newCertification, setNewCertification] = useState({ name: '', issuer: '', year: '', description: '' });
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showStripeOnboarding, setShowStripeOnboarding] = useState(false);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [bankAccountForm, setBankAccountForm] = useState({
    bank_name: '',
    account_type: 'checking',
    routing_number: '',
    account_number: ''
  });

  const timezoneOptions = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Dublin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Jakarta',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'
  ];

  const skillOptions = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 
    'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic',
    'Russian', 'Hindi', 'Dutch', 'Swedish', 'Norwegian'
  ];

  const expertiseOptions = [
    'Technology', 'Finance', 'Marketing', 'Sales', 'Healthcare',
    'Education', 'Law', 'Engineering', 'Design', 'Consulting',
    'Real Estate', 'Retail', 'Hospitality', 'Media', 'Non-profit'
  ];

  useEffect(() => {
    fetchProfile();
  }, [currentUser]);

  const fetchProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        setProfile(profileData);
        
        // Initialize arrays if they don't exist
        const editDataWithDefaults = {
          ...profileData,
          skills: profileData.skills || [],
          languages: profileData.languages || [],
          expertise: profileData.expertise || [],
          certifications: profileData.certifications || [],
          videoIntroUrl: profileData.videoIntroUrl || ''
        };
        setEditData(editDataWithDefaults);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, editData);
      setProfile(editData);
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelect = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: prev[field]?.includes(value) 
        ? prev[field].filter(item => item !== value)
        : [...(prev[field] || []), value]
    }));
  };

  const handleAddCertification = () => {
    const newCert = {
      id: Date.now(),
      name: '',
      issuer: '',
      year: '',
      description: ''
    };
    setEditData(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), newCert]
    }));
  };

  const handleUpdateCertification = (id, field, value) => {
    setEditData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).map(cert => 
        cert.id === id ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const handleRemoveCertification = (id) => {
    setEditData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).filter(cert => cert.id !== id)
    }));
  };

  // Stripe Connect Functions
  const handleSetupStripeConnect = async () => {
    // Show modal immediately for better UX
    setShowStripeOnboarding(true);
    
    try {
      setStripeLoading(true);
      
      const { accountId } = await createConnectAccount(
        currentUser.email,
        profile.firstName,
        profile.lastName
      );

      // Save the Stripe Connect account ID to Firebase
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, {
        stripeConnectAccountId: accountId
      });

      // Update local profile state
      setProfile(prev => ({ ...prev, stripeConnectAccountId: accountId }));
    } catch (error) {
      console.error('Error setting up Stripe Connect:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      });
      
      // Close modal if account creation failed
      setShowStripeOnboarding(false);
      alert(`Failed to set up payment account: ${error.message}. Please try again.`);
    } finally {
      setStripeLoading(false);
    }
  };


  const checkStripeConnectStatus = async () => {
    if (!profile.stripeConnectAccountId) {
      setStripeConnectStatus(null);
      return;
    }

    try {
      const status = await getConnectAccountStatus(profile.stripeConnectAccountId);
      setStripeConnectStatus(status);
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
      console.error('Status check error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      setStripeConnectStatus({ error: true });
    }
  };

  // Check Stripe Connect status when profile loads
  useEffect(() => {
    if (profile && profile.stripeConnectAccountId) {
      checkStripeConnectStatus();
    }
  }, [profile]);

  const handleOnboardingComplete = () => {
    setShowStripeOnboarding(false);
    // Refresh the account status
    checkStripeConnectStatus();
  };

  const handleOnboardingCancel = () => {
    setShowStripeOnboarding(false);
  };

  const handleChangeBankAccount = () => {
    // Pre-fill form with existing bank account details if available
    if (profile.bankAccountDetails) {
      setBankAccountForm({
        bank_name: profile.bankAccountDetails.bank_name || '',
        account_type: profile.bankAccountDetails.account_type || 'checking',
        routing_number: profile.bankAccountDetails.routing_number || '',
        account_number: ''
      });
    } else {
      setBankAccountForm({
        bank_name: '',
        account_type: 'checking',
        routing_number: '',
        account_number: ''
      });
    }
    setShowBankAccountModal(true);
  };

  const handleBankAccountFormChange = (field, value) => {
    setBankAccountForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBankAccount = async () => {
    if (!profile.stripeConnectAccountId) {
      alert('No Stripe account found. Please set up your payment account first.');
      return;
    }

    // Validate form
    if (!bankAccountForm.bank_name || !bankAccountForm.routing_number || !bankAccountForm.account_number) {
      alert('Please fill in all required fields.');
      return;
    }

    if (bankAccountForm.routing_number.length !== 9) {
      alert('Routing number must be 9 digits.');
      return;
    }

    if (bankAccountForm.account_number.length < 4) {
      alert('Account number must be at least 4 characters.');
      return;
    }

    try {
      setStripeLoading(true);
      
      await updateBankAccount(profile.stripeConnectAccountId, bankAccountForm);
      
      // Update local profile with new bank account details
      const accountNumber = bankAccountForm.account_number;
      const maskedAccountNumber = '*'.repeat(Math.max(0, accountNumber.length - 4)) + accountNumber.slice(-4);
      
      const updatedBankDetails = {
        bank_name: bankAccountForm.bank_name,
        account_type: bankAccountForm.account_type,
        routing_number: bankAccountForm.routing_number,
        account_number_masked: maskedAccountNumber,
        account_number_last4: accountNumber.slice(-4)
      };

      setProfile(prev => ({
        ...prev,
        bankAccountDetails: updatedBankDetails
      }));

      setShowBankAccountModal(false);
      alert('Bank account updated successfully!');
    } catch (error) {
      console.error('Error updating bank account:', error);
      
      // Use the specific error message from Stripe if available
      let errorMessage = error.message || 'Error updating bank account. Please try again.';
      
      // Check for common Stripe test account errors and provide helpful messages
      if (errorMessage.includes('test bank account number')) {
        // This is the specific error you mentioned - show it directly to the user
        errorMessage = errorMessage;
      } else if (errorMessage.includes('routing_number_invalid') || errorMessage.includes('Invalid routing number')) {
        errorMessage = 'Invalid bank routing number. For testing, please use routing number: 110000000';
      } else if (errorMessage.includes('account_number_invalid') && !errorMessage.includes('test bank account')) {
        errorMessage = 'Invalid bank account number. Please check and enter a valid account number.';
      }
      
      alert(errorMessage);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleCancelBankAccountChange = () => {
    setShowBankAccountModal(false);
    setBankAccountForm({
      bank_name: '',
      account_type: 'checking',
      routing_number: '',
      account_number: ''
    });
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="error">Profile not found</div>;
  }

  return (
    <div className="provider-profile">
      <div className="profile-container-wide">
        <div className="profile-header">
          <div className="header-content">
            <h2>Provider Dashboard</h2>
            {editing && (
              <div className="edit-actions">
                <button onClick={handleSave} className="save-profile-btn">
                  💾 Save Changes
                </button>
                <button 
                  onClick={() => {
                    setEditing(false);
                    const editDataWithDefaults = {
                      ...profile,
                      skills: profile.skills || [],
                      languages: profile.languages || [],
                      expertise: profile.expertise || [],
                      certifications: profile.certifications || [],
                      videoIntroUrl: profile.videoIntroUrl || ''
                    };
                    setEditData(editDataWithDefaults);
                  }} 
                  className="cancel-profile-btn"
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Availability Section - Full Width */}
        <div className="availability-section-full-width">
          <WeeklyAvailabilitySelector />
        </div>

        <div className="provider-content">
          <div className="profile-section-container">
            <div className="section-header">
              <h3>Profile Information</h3>
              {!editing && (
                <button onClick={() => setEditing(true)} className="edit-btn">
                  Edit Profile
                </button>
              )}
            </div>

            {editing ? (
              <div className="edit-form">
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={editData.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Hourly Rate ($)</label>
              <input
                type="number"
                value={editData.hourlyRate || ''}
                onChange={(e) => handleInputChange('hourlyRate', parseFloat(e.target.value))}
                min="5"
                max="500"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Age Bracket</label>
                <select
                  value={editData.ageBracket || ''}
                  onChange={(e) => handleInputChange('ageBracket', e.target.value)}
                  required
                >
                  <option value="">Select age bracket</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55-64">55-64</option>
                  <option value="65+">65+</option>
                </select>
              </div>

              <div className="form-group">
                <label>Gender</label>
                <select
                  value={editData.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Prefer Not to Say">Prefer Not to Say</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Experience/Qualifications</label>
              <textarea
                value={editData.experience || ''}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Timezone</label>
              <select 
                value={editData.timezone || ''}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                {timezoneOptions.map(tz => (
                  <option key={tz} value={tz}>
                    {tz.replace('_', ' ')} ({(() => {
                      try {
                        return new Date().toLocaleTimeString('en-US', { 
                          timeZone: tz, 
                          hour12: false, 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                      } catch (error) {
                        return 'Time unavailable';
                      }
                    })()})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Skills I can help with:</label>
              <div className="checkbox-grid">
                {skillOptions.map(skill => (
                  <label key={skill} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.skills?.includes(skill) || false}
                      onChange={() => handleMultiSelect('skills', skill)}
                    />
                    {skill}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Professional Expertise:</label>
              <div className="checkbox-grid">
                {expertiseOptions.map(area => (
                  <label key={area} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.expertise?.includes(area) || false}
                      onChange={() => handleMultiSelect('expertise', area)}
                    />
                    {area}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Languages I speak:</label>
              <div className="checkbox-grid">
                {languageOptions.map(language => (
                  <label key={language} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.languages?.includes(language) || false}
                      onChange={() => handleMultiSelect('languages', language)}
                    />
                    {language}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Video Introduction URL (optional)</label>
              <input
                type="url"
                value={editData.videoIntroUrl || ''}
                onChange={(e) => handleInputChange('videoIntroUrl', e.target.value)}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              />
              <small>Upload a short video introducing yourself to potential clients</small>
            </div>

            <div className="form-group">
              <label>Certifications & Credentials</label>
              {(editData.certifications || []).map(cert => (
                <div key={cert.id} className="certification-item">
                  <div className="cert-row">
                    <input
                      type="text"
                      placeholder="Certification Name"
                      value={cert.name || ''}
                      onChange={(e) => handleUpdateCertification(cert.id, 'name', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Issuing Organization"
                      value={cert.issuer || ''}
                      onChange={(e) => handleUpdateCertification(cert.id, 'issuer', e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Year"
                      value={cert.year || ''}
                      onChange={(e) => handleUpdateCertification(cert.id, 'year', e.target.value)}
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                    <button 
                      type="button" 
                      onClick={() => handleRemoveCertification(cert.id)}
                      className="remove-cert-btn"
                    >
                      ×
                    </button>
                  </div>
                  <textarea
                    placeholder="Description (optional)"
                    value={cert.description || ''}
                    onChange={(e) => handleUpdateCertification(cert.id, 'description', e.target.value)}
                    rows="2"
                  />
                </div>
              ))}
              <button type="button" onClick={handleAddCertification} className="add-cert-btn">
                + Add Certification
              </button>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} className="save-btn">
                Save Changes
              </button>
              <button 
                onClick={() => {
                  setEditing(false);
                  // Reset with proper defaults
                  const editDataWithDefaults = {
                    ...profile,
                    skills: profile.skills || [],
                    languages: profile.languages || [],
                    expertise: profile.expertise || [],
                    certifications: profile.certifications || [],
                    videoIntroUrl: profile.videoIntroUrl || ''
                  };
                  setEditData(editDataWithDefaults);
                }} 
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
            ) : (
              <div className="profile-display">
                <div className="profile-section">
                  <h3>Basic Information</h3>
                  <p><strong>Name:</strong> {profile.firstName} {profile.lastName}</p>
                  <p><strong>Age Bracket:</strong> {profile.ageBracket || 'Not specified'}</p>
                  <p><strong>Gender:</strong> {profile.gender || 'Not specified'}</p>
                  <p><strong>Hourly Rate:</strong> ${profile.hourlyRate}/hour</p>
                  <p><strong>Timezone:</strong> {profile.timezone ? profile.timezone.replace('_', ' ') : 'Not set'}</p>
                  {profile.timezone && (
                    <p><strong>Current Time:</strong> {(() => {
                      try {
                        // Handle legacy Mumbai timezone
                        const timezone = profile.timezone === 'Asia/Mumbai' ? 'Asia/Kolkata' : profile.timezone;
                        return new Date().toLocaleString('en-US', { 
                          timeZone: timezone,
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        });
                      } catch (error) {
                        return 'Time unavailable';
                      }
                    })()}</p>
                  )}
                </div>

                <div className="profile-section">
                  <h3>About</h3>
                  <p>{profile.bio}</p>
                </div>

                {profile.videoIntroUrl && (
                  <div className="profile-section">
                    <h3>Video Introduction</h3>
                    <div className="video-intro">
                      {profile.videoIntroUrl.includes('youtube.com') || profile.videoIntroUrl.includes('youtu.be') ? (
                        <iframe
                          width="100%"
                          height="315"
                          src={profile.videoIntroUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                          title="Video Introduction"
                          frameBorder="0"
                          allowFullScreen
                        ></iframe>
                      ) : profile.videoIntroUrl.includes('vimeo.com') ? (
                        <iframe
                          width="100%"
                          height="315"
                          src={profile.videoIntroUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                          title="Video Introduction"
                          frameBorder="0"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <p><a href={profile.videoIntroUrl} target="_blank" rel="noopener noreferrer">View Video Introduction</a></p>
                      )}
                    </div>
                  </div>
                )}

                {profile.skills && profile.skills.length > 0 && (
                  <div className="profile-section">
                    <h3>Skills</h3>
                    <div className="tags">
                      {profile.skills.map(skill => (
                        <span key={skill} className="tag skill-tag">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {profile.expertise && profile.expertise.length > 0 && (
                  <div className="profile-section">
                    <h3>Professional Expertise</h3>
                    <div className="tags">
                      {profile.expertise.map(area => (
                        <span key={area} className="tag expertise-tag">{area}</span>
                      ))}
                    </div>
                  </div>
                )}

                {profile.languages && profile.languages.length > 0 && (
                  <div className="profile-section">
                    <h3>Languages</h3>
                    <div className="tags">
                      {profile.languages.map(lang => (
                        <span key={lang} className="tag language-tag">{lang}</span>
                      ))}
                    </div>
                  </div>
                )}

                {profile.experience && (
                  <div className="profile-section">
                    <h3>Experience & Qualifications</h3>
                    <p>{profile.experience}</p>
                  </div>
                )}

                {profile.certifications && profile.certifications.length > 0 && (
                  <div className="profile-section">
                    <h3>Certifications & Credentials</h3>
                    <div className="certifications-list">
                      {profile.certifications.map((cert, index) => (
                        <div key={cert.id || index} className="certification-display">
                          <div className="cert-header">
                            <h4>{cert.name}</h4>
                            <span className="cert-year">{cert.year}</span>
                          </div>
                          <p className="cert-issuer">{cert.issuer}</p>
                          {cert.description && <p className="cert-description">{cert.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="right-column-wrapper">
            <div className="stripe-connect-section-container">
            <div className="section-header">
              <h3>Payment Account</h3>
            </div>
            <div className="stripe-connect-status">
              {!profile.stripeConnectAccountId ? (
                <div className="stripe-not-connected">
                  <div className="status-info">
                    <div className="status-icon">💳</div>
                    <div className="status-text">
                      <h4>Set up your payment account</h4>
                      <p>Connect with Stripe to receive payments from clients securely.</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleSetupStripeConnect}
                    disabled={stripeLoading}
                    className="setup-stripe-btn"
                  >
                    {stripeLoading ? 'Setting up...' : 'Set up payment account'}
                  </button>
                </div>
              ) : (
                <div className="stripe-connected">
                  <div className="status-info">
                    <div className="status-icon">✅</div>
                    <div className="status-text">
                      <h4>Payment account connected</h4>
                      {stripeConnectStatus ? (
                        <div className="connect-details">
                          <p><strong>Status:</strong> {stripeConnectStatus.detailsSubmitted ? 'Complete' : 'Setup Required'}</p>
                          <p><strong>Payments:</strong> {stripeConnectStatus.chargesEnabled ? 'Enabled' : 'Disabled'}</p>
                          <p><strong>Payouts:</strong> {stripeConnectStatus.payoutsEnabled ? 'Enabled' : 'Disabled'}</p>
                          {stripeConnectStatus.requirementsCurrentlyDue && stripeConnectStatus.requirementsCurrentlyDue.length > 0 && (
                            <p className="requirements-notice">
                              <strong>Action Required:</strong> Please complete your account setup
                            </p>
                          )}
                        </div>
                      ) : (
                        <p>Loading account status...</p>
                      )}
                    </div>
                  </div>
                  <div className="stripe-actions">
                    {stripeConnectStatus && !stripeConnectStatus.detailsSubmitted && (
                      <button 
                        onClick={handleSetupStripeConnect}
                        disabled={stripeLoading}
                        className="complete-setup-btn"
                      >
                        Complete Setup
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

            {/* Bank Account Details Section */}
            {profile.stripeConnectAccountId && profile.bankAccountDetails && (
              <div className="bank-account-section-container">
                <div className="section-header">
                  <h3>Bank Account Details</h3>
                </div>
                <div className="bank-account-info">
                  <div className="bank-details-grid">
                    <div className="bank-detail-item">
                      <label>Bank Name</label>
                      <span>{profile.bankAccountDetails.bank_name}</span>
                    </div>
                    <div className="bank-detail-item">
                      <label>Account Type</label>
                      <span>{profile.bankAccountDetails.account_type}</span>
                    </div>
                    <div className="bank-detail-item">
                      <label>Account Number</label>
                      <span>{profile.bankAccountDetails.account_number_masked}</span>
                    </div>
                    <div className="bank-detail-item">
                      <label>Routing Number</label>
                      <span>{profile.bankAccountDetails.routing_number}</span>
                    </div>
                  </div>
                  <button 
                    onClick={handleChangeBankAccount}
                    className="change-bank-account-btn"
                    disabled={stripeLoading}
                  >
                    Change Bank Account
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {showStripeOnboarding && (
        <StripeOnboarding
          accountId={profile.stripeConnectAccountId}
          onComplete={handleOnboardingComplete}
          onCancel={handleOnboardingCancel}
          userProfile={{
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: currentUser.email
          }}
        />
      )}
      
      {showBankAccountModal && (
        <div className="bank-account-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Change Bank Account</h3>
              <button onClick={handleCancelBankAccountChange} className="close-btn">×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Bank Name *</label>
                <input
                  type="text"
                  value={bankAccountForm.bank_name}
                  onChange={(e) => handleBankAccountFormChange('bank_name', e.target.value)}
                  placeholder="Bank of America, Chase, Wells Fargo, etc."
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Account Type *</label>
                <select
                  value={bankAccountForm.account_type}
                  onChange={(e) => handleBankAccountFormChange('account_type', e.target.value)}
                  required
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Routing Number *</label>
                <input
                  type="text"
                  maxLength="9"
                  value={bankAccountForm.routing_number}
                  onChange={(e) => handleBankAccountFormChange('routing_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="110000000"
                  required
                />
                <small>9-digit routing number. For testing, use: 110000000</small>
              </div>
              
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  type="text"
                  value={bankAccountForm.account_number}
                  onChange={(e) => handleBankAccountFormChange('account_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="000123456789"
                  required
                />
                <small>Your account number. For testing, use: 000123456789</small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button onClick={handleCancelBankAccountChange} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={handleSaveBankAccount} 
                disabled={stripeLoading}
                className="btn-primary"
              >
                {stripeLoading ? 'Updating...' : 'Update Bank Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderProfile;